/**
 * Shell de autenticación del panel del operador de plataforma (doc 03 Fase
 * 3) — copia adaptada de `frontend/src/App.tsx`: mismo flujo de login
 * Cognito SRP + MFA, pero sin `/me` (el operador no tiene `org_id`/roles que
 * resolver — la posesión de un JWT válido de este pool ya es la
 * autorización completa, ver `backend/src/lib/identidad-operador.ts`), así
 * que pasa directo a `PanelOperador` tras un login exitoso.
 */
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { config, configCompleta } from './config.js';
import {
  iniciarSesion,
  cerrarSesion,
  sesionActual,
  type ResultadoLogin,
  type Sesion,
} from './auth.js';
import { PanelOperador } from './PanelOperador.js';

type ContextoMfa = Extract<ResultadoLogin, { tipo: 'mfa_verificar' | 'mfa_configurar' }>;
type ContextoNuevaClave = Extract<ResultadoLogin, { tipo: 'nueva_clave' }>;

type Estado =
  | { fase: 'cargando' }
  | { fase: 'sin_config' }
  | { fase: 'login'; error?: string }
  | { fase: 'nueva_clave'; contexto: ContextoNuevaClave; error?: string }
  | { fase: 'mfa'; contexto: ContextoMfa; error?: string }
  | { fase: 'autenticado'; sesion: Sesion };

export function App() {
  const [estado, setEstado] = useState<Estado>({ fase: 'cargando' });

  useEffect(() => {
    if (!configCompleta()) {
      setEstado({ fase: 'sin_config' });
      return;
    }
    void (async () => {
      const sesion = await sesionActual();
      setEstado(sesion ? { fase: 'autenticado', sesion } : { fase: 'login' });
    })();
  }, []);

  if (estado.fase === 'cargando')
    return (
      <Marco>
        <p>Cargando…</p>
      </Marco>
    );
  if (estado.fase === 'sin_config') return <SinConfig />;

  async function manejarResultado(resultado: ResultadoLogin) {
    if (resultado.tipo === 'ok') {
      setEstado({ fase: 'autenticado', sesion: resultado.sesion });
    } else if (resultado.tipo === 'nueva_clave') {
      setEstado({ fase: 'nueva_clave', contexto: resultado });
    } else {
      setEstado({ fase: 'mfa', contexto: resultado });
    }
  }

  if (estado.fase === 'login') {
    return (
      <Login
        error={estado.error}
        onLogin={async (email, pass) => {
          try {
            await manejarResultado(await iniciarSesion(email, pass));
          } catch (e) {
            setEstado({ fase: 'login', error: mensajeError(e) });
          }
        }}
      />
    );
  }

  if (estado.fase === 'nueva_clave') {
    return (
      <NuevaClave
        error={estado.error}
        onCompletar={async (claveNueva) => {
          try {
            await manejarResultado(await estado.contexto.completar(claveNueva));
          } catch (e) {
            setEstado({ fase: 'nueva_clave', contexto: estado.contexto, error: mensajeError(e) });
          }
        }}
        onCancelar={() => setEstado({ fase: 'login' })}
      />
    );
  }

  if (estado.fase === 'mfa') {
    return (
      <Mfa
        contexto={estado.contexto}
        error={estado.error}
        onVerificar={async (codigo) => {
          try {
            const sesion = await estado.contexto.verificar(codigo);
            setEstado({ fase: 'autenticado', sesion });
          } catch (e) {
            setEstado({ fase: 'mfa', contexto: estado.contexto, error: mensajeError(e) });
          }
        }}
        onCancelar={() => setEstado({ fase: 'login' })}
      />
    );
  }

  return (
    <PanelOperador
      idToken={estado.sesion.idToken}
      onSalir={() => {
        cerrarSesion();
        setEstado({ fase: 'login' });
      }}
    />
  );
}

function Marco({ children }: { children: React.ReactNode }) {
  return (
    <main className="marco">
      <header className="cabecera">
        <img src="/logo-intikipu.png" alt="IntiKipu" className="logo-img" />
        <div>
          <h1>Instructor — Operador</h1>
          <p className="subtitulo">Gestión de instituciones, licencias y administradores</p>
        </div>
      </header>
      {config.ambiente === 'demo' && <div className="banner-demo">PANEL DEL ENTORNO DEMO</div>}
      {children}
    </main>
  );
}

function SinConfig() {
  return (
    <Marco>
      <div className="tarjeta aviso">
        <h2>Configuración pendiente</h2>
        <p>
          Falta definir <code>VITE_COGNITO_USER_POOL_ID</code>, <code>VITE_COGNITO_CLIENT_ID</code>{' '}
          y <code>VITE_API_URL</code> del pool/API del OPERADOR (distintos de los del organismo).
        </p>
      </div>
    </Marco>
  );
}

function NuevaClave({
  error,
  onCompletar,
  onCancelar,
}: {
  error?: string | undefined;
  onCompletar: (claveNueva: string) => Promise<void>;
  onCancelar: () => void;
}) {
  const [clave, setClave] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [enviando, setEnviando] = useState(false);

  const noCoinciden = confirmacion.length > 0 && clave !== confirmacion;

  return (
    <Marco>
      <form
        className="tarjeta"
        onSubmit={async (e) => {
          e.preventDefault();
          if (clave !== confirmacion) return;
          setEnviando(true);
          await onCompletar(clave);
          setEnviando(false);
        }}
      >
        <h2>Elegir clave definitiva</h2>
        <p className="ayuda">
          Primer ingreso: la clave temporal que recibió por correo debe reemplazarse. Mínimo 12
          caracteres, con mayúscula, minúscula, número y símbolo.
        </p>
        <label>
          Clave nueva
          <input
            type="password"
            value={clave}
            onChange={(e) => setClave(e.target.value)}
            autoComplete="new-password"
            minLength={12}
            autoFocus
            required
          />
        </label>
        <label>
          Confirmar clave nueva
          <input
            type="password"
            value={confirmacion}
            onChange={(e) => setConfirmacion(e.target.value)}
            autoComplete="new-password"
            minLength={12}
            required
          />
        </label>
        {noCoinciden && <p className="error">Las claves no coinciden.</p>}
        {error && <p className="error">{error}</p>}
        <div className="fila-entre">
          <button type="button" className="secundario" disabled={enviando} onClick={onCancelar}>
            Cancelar
          </button>
          <button type="submit" disabled={enviando || noCoinciden}>
            {enviando ? 'Guardando…' : 'Continuar'}
          </button>
        </div>
      </form>
    </Marco>
  );
}

function Login({
  error,
  onLogin,
}: {
  error?: string | undefined;
  onLogin: (email: string, pass: string) => Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [enviando, setEnviando] = useState(false);

  return (
    <Marco>
      <form
        className="tarjeta"
        onSubmit={async (e) => {
          e.preventDefault();
          setEnviando(true);
          await onLogin(email, pass);
          setEnviando(false);
        }}
      >
        <h2>Ingresar</h2>
        <label>
          Correo del operador
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
          />
        </label>
        <label>
          Clave
          <input
            type="password"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        {error && <p className="error">{error}</p>}
        <button type="submit" disabled={enviando}>
          {enviando ? 'Ingresando…' : 'Ingresar'}
        </button>
      </form>
    </Marco>
  );
}

function Mfa({
  contexto,
  error,
  onVerificar,
  onCancelar,
}: {
  contexto: ContextoMfa;
  error?: string | undefined;
  onVerificar: (codigo: string) => Promise<void>;
  onCancelar: () => void;
}) {
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (contexto.tipo !== 'mfa_configurar') {
      setQrDataUrl(null);
      return;
    }
    let cancelado = false;
    QRCode.toDataURL(contexto.otpauthUri, { margin: 1, width: 200 })
      .then((url) => {
        if (!cancelado) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelado) setQrDataUrl(null);
      });
    return () => {
      cancelado = true;
    };
  }, [contexto]);

  return (
    <Marco>
      <form
        className="tarjeta"
        onSubmit={async (e) => {
          e.preventDefault();
          setEnviando(true);
          await onVerificar(codigo);
          setEnviando(false);
        }}
      >
        <h2>Verificación en dos pasos</h2>
        {contexto.tipo === 'mfa_configurar' && (
          <>
            <p className="ayuda">
              Primer ingreso: escanee este código con una app autenticadora (Google Authenticator,
              Authy, etc.).
            </p>
            {qrDataUrl && (
              <img
                src={qrDataUrl}
                width={200}
                height={200}
                alt="Código QR para configurar la app autenticadora"
                className="qr-mfa"
              />
            )}
            <p className="ayuda">¿Sin cámara a mano? Ingrese esta clave manualmente:</p>
            <p className="ayuda hash secreto-mfa">{contexto.secreto}</p>
          </>
        )}
        <label>
          Código de 6 dígitos
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            autoComplete="one-time-code"
            autoFocus
            required
          />
        </label>
        {error && <p className="error">{error}</p>}
        <div className="fila-entre">
          <button type="button" className="secundario" disabled={enviando} onClick={onCancelar}>
            Cancelar
          </button>
          <button type="submit" disabled={enviando}>
            {enviando ? 'Verificando…' : 'Verificar'}
          </button>
        </div>
      </form>
    </Marco>
  );
}

export function mensajeError(e: unknown): string {
  if (e instanceof Error) return e.message;
  return 'Ocurrió un error inesperado.';
}
